import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs-make";
import "./component.scss"

export default class extends Controller {
  static targets = [
    "item",
    "galleryContainer",
    "thumbsGalleryContainer",
    "thumbsItem",
    "videoIframe",
    "editVideoButtonContainer",
    "editVideoButton",
    "replayButton"
  ];

  static outlets = [ "bunny-player" ];

  static values = {
    itemClass: String,
    muteIconClass: String,
    canEdit: Boolean
  }

  connect() {
    this.container = this.galleryContainerTarget;
    this.activeIndex = null;

    this.setupSortable();
    this.setupObserver();
  }

  disconnect() {
    this.destroySortable()
    this.removeObserver()
  }

  // Sortable JS related methods ----------------
  setupSortable() {
    if (this.hasThumbsGalleryContainerTarget && this.canEditValue) {
      this.initializeSortable();
    }
  }

  destroySortable() {
    if (this.sortable) {
      this.sortable.destroy();
    }
  }

  initializeSortable() {
    this.sortable = Sortable.create(this.thumbsGalleryContainerTarget, {
      animation: 150,
      filter: ".gallery_item_form_button",
      swapThreshold: 0.8,
      onEnd: this.onReorder.bind(this),
      onMove: this.onMove.bind(this)
    });
  }

  onReorder(event) {
    const data = new FormData();

    const id = event.item.dataset.id;
    const newIndex = event.newIndex;
    const url = `/gallery_items/${id}/reorder`

    // Sortable JS uses zero-based indexing, but our positions are one-based
    // BUT there is a form button at the start we need to account for.
    // Otherwise, we would have to update with newIndex + 1
    data.append("gallery_item[new_position]", newIndex);

    fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: data,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response;
      })
      .catch((error) => {
        console.error("There was a problem with the fetch operation:", error);
      });
  }

  onMove(event, originalEvent) {
    const list = event.to;
    const first = list.firstElementChild;
    const isForm = first && first.classList.contains("gallery_item_form_button");

    if (isForm && !event.willInsertAfter && event.related === first) {
      // Prevent moving infront of the form button
      // Moving the form button is prevented by the filter option
      return false;
    }
  }

  // Drag Scroll ----------------

  scrollToIndex(event) {
    const item = event.currentTarget;
    const container = this.container;
    const id = parseInt(item.dataset.id);

    if(!id) return;

    const targetItem = this.itemTargets.find(item => parseInt(item.dataset.id) === id);

    container.scrollTo({
      left: targetItem.offsetLeft,
      behavior: "smooth",
    })
  }

  scrollThumbsToIndex(index) {
    const items = this.thumbsItemTargets

    if (index < 0 || index >= items.length) return

    const targetItem = items[index]

    const offsetLeft = targetItem.offsetLeft
    const offsetWidth = targetItem.offsetWidth

    this.thumbsGalleryContainerTarget.scrollTo({
      left: offsetLeft - (this.thumbsGalleryContainerTarget.offsetWidth / 2) + (offsetWidth / 2),
      behavior: "smooth",
    })
  }

  onActiveItemChanged(item, index) {
    this.forcePauseAllVideos()

    this.playItem(item)
    this.updateMuteButton(item)

    this.scrollThumbsToIndex(index)
    // this.updateActiveButtonStates(index)
    this.updateVideoEditButton(item)
  }

  // Observers ----------------
  setupObserver() {
    this.observer = new IntersectionObserver((entries) => this.handleIntersections(entries),
      { root: this.container,
        threshold: 0.6
      }
    );
    this.itemTargets.forEach((item) => this.observer.observe(item));
  }

  removeObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  handleIntersections(entries) {
    let bestEntry = null;

    entries.forEach((entry) => {
      if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
        bestEntry = entry;
      }
    });

    if (!bestEntry || !bestEntry.isIntersecting) return;

    const newIndex = this.itemTargets.indexOf(bestEntry.target);
    if (newIndex === -1 || newIndex === this.activeIndex) return;

    this.activeIndex = newIndex;
    this.onActiveItemChanged(bestEntry.target, newIndex)
  }

  // Video Players ----------------
  // Playback API via bunny_player component
  // Use outlets to control playback

  forcePauseAllVideos() {
    this.bunnyPlayerOutlets.forEach((outlet) => {
      outlet.pause();
    })
  }

  playItem(item) {
    const outlet = this.outletFor(item);
    if (outlet) {
      outlet.play();
    }
  }

  toggleMute(event) {
    event.preventDefault();
    event.stopPropagation();

    const item = event.currentTarget.closest(`.${this.itemClassValue}`);
    const outlet = this.outletFor(item);

    if (outlet) {
      outlet.toggleMute(muted => {
        this.updateMuteButton(item, muted);
      });
    }
  }

  updateMuteButton(item, muted = null) {
    const muteIcon = this.muteIconFor(item);
    const outlet = this.outletFor(item);

    if(outlet && muteIcon) {
      if (muted !== null) {
        muteIcon.textContent = muted ? "🔇" : "🔊";
        return;
      }

      outlet.player.getMuted(muted => {
        muteIcon.textContent = muted ? "🔇" : "🔊";
      });
    }
  }

  replayVideo(event) {
    event.preventDefault();
    event.stopPropagation();

    const item = event.currentTarget.closest(`.${this.itemClassValue}`);
    const outlet = this.outletFor(item);
    if (outlet) {
      this.hideReplayButton(item);
      outlet.replay();
    }
  }

  updateActiveButtonStates(index) {
    const activeClass = "c-gallery-thumb-button--active";
    const buttonClass = "c-gallery-thumb-button";

    const allThumbs = this.element.querySelectorAll(`.${buttonClass}`);
    allThumbs.forEach((thumb) =>
      thumb.classList.remove(activeClass)
    );
    const activeElement = allThumbs[index];
    activeElement.classList.add(activeClass);
  }

  updateVideoEditButton(item) {
    if (this.hasEditVideoButtonContainerTarget) {
      const isVideo = item.hasAttribute("data-video")
      const galleryItemId = item.dataset.id
      if (isVideo) {
        this.editVideoButtonTarget.href = `/gallery_items/${galleryItemId}/edit`;
        this.editVideoButtonContainerTarget.removeAttribute("data-hidden");
      } else {
        this.editVideoButtonTarget.href = `#`;
        this.editVideoButtonContainerTarget.setAttribute("data-hidden", "true");
      }
    }
  }

  // Replay button methods
  showReplayButton(item) {
    const replayButton = this.replayButtonFor(item);
    if (replayButton) {
      replayButton.removeAttribute("data-hidden");
    }
  }

  hideReplayButton(item) {
    const replayButton = this.replayButtonFor(item);
    if (replayButton) {
      replayButton.setAttribute("data-hidden", "true");
    }
  }

  videoEnded(event) {
    const player = event.target

    const item = player.closest(`.${this.itemClassValue}`);
    this.showReplayButton(item);
  }

  outletFor(item) {
    const playerElement = item.querySelector("[data-controller~='bunny-player']");
    const outlet = this.bunnyPlayerOutlets.find((bunnyPlayer) => bunnyPlayer.element === playerElement);

    return outlet;
  }

  replayButtonFor(item) {
    return item.querySelector('[data-gallery-target="replayButton"]');
  }

  muteIconFor(item) {
    return item.querySelector(`.${this.muteIconClassValue}`);
  }
}

